// app/singup/page.tsx
"use client";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
//import { useRouter } from "next/navigation";
import OtpVerify from "@/components/auth/OtpVerify";
import { apiRegister, apiVerifyOtp, AuthApiError } from "@/lib/auth-api";
import { setAccessToken } from "@/lib/auth";
import { useChatStore } from "@/store/chatStore";
import { collectFingerprint } from "@/lib/fingerprint";
import { useTranslations } from "next-intl";
import { useAuth } from "@/components/auth/auth-context";
import HexLive from "@/components/HexLive";
import { env } from "@/lib/env";

type Step = "credentials" | "otp" | "success";

export default function SignUpPage() {
  //const router = useRouter();
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [otpError, setOtpError] = useState("");
  const [pwStrength, setPwStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState(""); // П лв
  const [city, setCity] = useState("");         // Вт лв
  const s = useTranslations("signup");
  const tc = useTranslations("common");
  const honeypotName = useRef<HTMLInputElement>(null);
  const honeypotCity = useRef<HTMLInputElement>(null);

  const { isAuthenticated, isLoading } = useAuth();

 // useEffect(() => {
 //   if (!isLoading && isAuthenticated) {
 //     window.location.replace(window.location.origin + "/chat");
//    }
//  }, [isAuthenticated, isLoading]);

  const calcStrength = (pw: string) => {
      let s = 0;
      if (pw.length >= 8) s++;
      if (/[A-Z]/.test(pw)) s++;
      if (/[a-z]/.test(pw)) s++; // Добавили строчные
      if (/[0-9]/.test(pw)) s++;
      if (/[^A-Za-z0-9]/.test(pw)) s++;
      
      // Ограничиваем максимум 4 баллами для твоих цветов
      setPwStrength(Math.min(s, 4)); 
    };

  const strengthLabel = ["", s("strengthWeak"), s("strengthMedium"), s("strengthGood"), s("strengthStrong")][pwStrength];
  const strengthColor = ["", "bg-red-400", "bg-amber-400", "bg-blue-400", "bg-green-500"][pwStrength];

  //const validate = () => {
  //  const e: Record<string, string> = {};
  //  if (!email) e.email = "Введите email";
  //  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Некорректный email";
  //  if (!password) e.password = "Введите пароль";
  //  else if (password.length < 8) e.password = "Минимум 8 символов";
  //  if (password !== confirm) e.confirm = "Пароли не совпадают";
  //  if (!tosAccepted) e.tos = "Необходимо принять условия";
  //  setErrors(e);
  //  return Object.keys(e).length === 0;
 // }; 
    const validate = () => {
        const e: Record<string, string> = {};
        
        if (!email) e.email = s("errors.emailRequired");
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = s("errors.emailInvalid");

        if (!password) {
          e.password = s("errors.passwordRequired");
        } else {
          // Проверяем все твои условия разом
          const hasUpper = /[A-Z]/.test(password);
          const hasLower = /[a-z]/.test(password);
          const hasNumber = /[0-9]/.test(password);
          const hasSymbol = /[^A-Za-z0-9]/.test(password);

          if (password.length < 8) {
            e.password = s("errors.passwordMin");
          } else if (!hasUpper || !hasLower || !hasNumber || !hasSymbol) {
            e.password = s("errors.passwordWeak")
          }
        }

        if (password !== confirm) e.confirm = s("errors.passwordMismatch");
        if (!tosAccepted) e.tos = s("errors.tosRequired");

        setErrors(e);
        return Object.keys(e).length === 0;
      };


  const handleRegister = async () => {

    // Если хотя бы одно поле заполнено — перед нами бот
          if (honeypotName.current?.value || honeypotCity.current?.value) {
            console.warn("Anti-bot: Honeypot triggered");
            // Имитируем успех: переводим на следующий шаг, но реально API не вызываем
            setStep("success"); 
            return;
          }

    if (!validate()) return;
    setLoading(true);
    setErrors({});
    try {
      const fp = await collectFingerprint();
      const res = await apiRegister({ email, password, fingerprintHash: fp.hash });
      //setAccessToken(res.accessToken);
      //saveRememberMe(false); // при регистрации rememberMe всегда false
      //saveTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken, rememberMe: false });
      // TODO: когда OTP будет на бэке — убрать router.push и раскомментить setStep("otp")
      localStorage.setItem('is_new_user', '1');
       setStep("otp");
      //router.push("/chat");
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.status === 409) setErrors({ email: s("errors.emailTaken") });
        else if (err.status === 403) setErrors({ general: s("errors.registrationClosed") });
        else setErrors({ general: err.message });
      } else {
        setErrors({ general: s("errors.network") });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    setOtpError("");
    try {
      const res = await apiVerifyOtp({ email, code });
      setAccessToken(res.accessToken);
      useChatStore.getState().setActiveChat(null);
      useChatStore.setState({ activeChatSessionId: null, sessions: [] });
      window.location.assign(window.location.origin + "/chat");
      //saveRememberMe(false); // при регистрации rememberMe всегда false
     // saveTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken, rememberMe: false });
      
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.status === 410) setOtpError(s("errors.otpExpired"));
        else if (err.status === 429) setOtpError(s("errors.otpRateLimit"));
        else setOtpError(s("errors.otpInvalid"));
      } else {
        setOtpError(s("errors.network"));
      }
    }
  };

  const handleResendOtp = async () => {
    await apiRegister({ email, password });
  };

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      {/* ── Left: Form ── */}
      <div className="flex-1 h-full flex flex-col px-6 py-4 lg:px-16 lg:py-8 overflow-y-auto custom-scrollbar">
        {/* Logo */}
        <div className="flex items-center justify-between mb-4 lg:mb-10">
          <Link href="/" className="text-lg font-semibold tracking-widest text-zinc-900 uppercase">
              {tc("brand")}
            </Link>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center py-2 lg:py-6">
          <div className="w-full max-w-sm my-auto space-y-1">
            {step === "credentials" && (
              <>
                <h1 className="text-xl lg:text-2xl font-semibold text-zinc-900 tracking-tight mb-1">
                  {s("title")}
                </h1>
                <p className="text-sm text-zinc-500 mb-4 lg:mb-6">
                  {s("hasAccount")}{" "}
                  <Link href="/signin" className="text-zinc-900 font-medium underline underline-offset-2 hover:no-underline">
                    {s("signin")}
                  </Link>
                </p>

                {/* OAuth */}
                <div className="space-y-2 mb-4">
                 {/* <button
                    disabled
                    className="w-full flex items-center justify-center gap-3 py-2 px-4 rounded-xl bg-zinc-900 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Регистрация через X
                    <span className="ms-auto text-xs text-zinc-500 font-normal">скоро</span>
                  </button> */}

                     <button
                        type="button"
                          onClick={() => { sessionStorage.removeItem("oauth_callback_processed"); window.location.href = `${env.apiUrl}/auth/google`; }}
                        className="w-full flex items-center justify-center gap-3 py-2 px-4 rounded-xl border border-zinc-200 text-zinc-700 text-sm font-medium hover:border-zinc-300 hover:bg-zinc-50 transition-all"
                      >
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {s("googleBtn")}
                    {/* <span className="ms-auto text-xs text-zinc-400 font-normal">скоро</span> */}
                  </button>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-zinc-100" />
                  <span className="text-xs text-zinc-400">{s("orEmail")}</span>
                  <div className="flex-1 h-px bg-zinc-100" />
                </div>

                {/* General error */}
                {errors.general && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600 flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
                    </svg>
                    {errors.general}
                  </div>
                )}

                  {/* --- П ЛВ --- */}
                    <div className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden" aria-hidden="true">
                      <input
                        type="text"
                        name="full_name"
                       ref={honeypotName}
                        defaultValue=""
                        onChange={() => {}}
                        tabIndex={-1}
                        autoComplete="new-password"
                      />
                    </div>
                {/* Email */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-zinc-600 mb-1.5">{s("emailLabel")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                    placeholder={s("emailPlaceholder")}
                    className={`w-full px-4 py-2 rounded-xl border text-sm outline-none transition-all
                      ${errors.email ? "border-red-300 bg-red-50" : "border-zinc-200 focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"}`}
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                {/* Password */}
                {/* Password */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-zinc-600 mb-1.5">{s("passwordLabel")}</label>
                      
                      {/* Обертка только для инпута и кнопки */}
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"} // Динамический тип
                          value={password}
                          onChange={(e) => { 
                            setPassword(e.target.value); 
                            calcStrength(e.target.value); 
                            setErrors((p) => ({ ...p, password: "" })); 
                          }}
                          autoComplete="new-password"
                          placeholder={s("placeholderPassword")}
                          className={`w-full px-4 py-2 pe-10 rounded-xl border text-sm outline-none transition-all
                            ${errors.password ? "border-red-300 bg-red-50" : "border-zinc-200 focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"}`}
                        />
                        
                        {/* Кнопка-глазик внутри инпута */}
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute end-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                        >
                          {showPassword ? (
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                          ) : (
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          )}
                        </button>
                      </div>

                      {/* Твои ошибки и шкала остаются на месте под инпутом */}
                      {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}

                      {/* Strength bar */}
                      {password && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 flex gap-1">
                            {[1, 2, 3, 4].map((n) => (
                              <div
                                key={n}
                                className={`h-1 flex-1 rounded-full transition-all ${n <= pwStrength ? strengthColor : "bg-zinc-100"}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-zinc-400">{strengthLabel}</span>
                        </div>
                      )}
                    </div>

                {/* Confirm */}
                <div className="mb-5">
                  <label className="block text-xs font-medium text-zinc-600 mb-1.5">{s("confirmLabel")}</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setErrors((p) => ({ ...p, confirm: "" })); }}
                    placeholder={s("placeholderConfirm")}
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-all
                      ${errors.confirm ? "border-red-300 bg-red-50" : "border-zinc-200 focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"}`}
                  />
                  {errors.confirm && <p className="text-xs text-red-500 mt-1">{errors.confirm}</p>}
                </div>

                {/* ToS checkbox */}
                <label className={`flex items-start gap-2.5 mb-4 cursor-pointer group ${errors.tos ? "text-red-500" : ""}`}>
                        {/* 1. Скрытый настоящий инпут для логики */}
                        <input 
                          type="checkbox"
                          className="sr-only"
                          checked={tosAccepted}
                          onChange={() => {
                            setTosAccepted(!tosAccepted);
                            setErrors((p) => ({ ...p, tos: "" }));
                          }}
                        />

                        {/* 2. Визуальный кастомный квадратик (без onClick!) */}
                        <div className={`mt-0.5 w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-all
                          ${tosAccepted ? "bg-zinc-900 border-zinc-900" : errors.tos ? "border-red-400" : "border-zinc-300 group-hover:border-zinc-500"}`}
                        >
                          {tosAccepted && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="2 6 5 9 10 3"/>
                            </svg>
                          )}
                        </div>

                        {/* 3. Текст со ссылками */}
                        <span className="text-xs text-zinc-600 select-none leading-relaxed">
                          {s("tosText")}{" "}
                          <Link href="/terms" className="underline underline-offset-2 hover:text-zinc-900 font-medium" onClick={(e: React.MouseEvent) => e.stopPropagation()} // Чтобы клик по ссылке не дергал чекбокс
                          >
                            {s("tosLink")}
                          </Link>{" "}
                          {" "}{s("andText")}{" "}
                          <Link 
                            href="/privacy" 
                            className="underline underline-offset-2 hover:text-zinc-900 font-medium" 
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            {s("privacyLink")}
                          </Link>
                          {s("tosNote")}
                        </span>
                      </label>
                      {/* Вывод ошибки ниже */}
                      {errors.tos && <p className="text-xs text-red-500 -mt-2 mb-4">{errors.tos}</p>}
                            {/* --- В ЛВ (ToS) --- */}
                        <div className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden" aria-hidden="true">
                          <input
                            type="text"
                            name="user_city"
                            ref={honeypotCity}
                              defaultValue=""
                              onChange={() => {}}
                            tabIndex={-1}
                            autoComplete="new-password"
                          />
                        </div>
                {/* Submit */}
                <button
                  onClick={handleRegister}
                  disabled={loading}
                  className="w-full py-2 bg-zinc-900 text-white text-sm font-medium rounded-xl hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {s("submitting")}
                    </>
                  ) : s("submit")}
                </button>
              </>
            )}

            {step === "success" && (
              <div className="text-center animate-in fade-in zoom-in duration-300">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-zinc-900 mb-2">Почти готово!</h2>
                <p className="text-sm text-zinc-500">
                  {s("otpSentDesc")}
                </p>
              </div>
            )}

            {step === "otp" && (
              <OtpVerify
                email={email}
                onVerify={handleVerifyOtp}
                onResend={handleResendOtp}
                onBack={() => setStep("credentials")}
                error={otpError}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Right: Illustration (same as signin) ── */}
      {/* ── Right: Illustration ── */}

      {/** <div className="hidden lg:flex flex-1 bg-[#0d1b35] items-center justify-center relative overflow-hidden h-screen sticky top-0">
        -- Fallback фон — звёзды, если картинка не загрузится --
        {[...Array(18)].map((_, i) => (
          <div
            key={i}
            className="absolute pointer-events-none"
            style={{
              
            }}
          >
            <svg width={i % 3 === 0 ? 14 : 8} height={i % 3 === 0 ? 14 : 8} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </div>
        ))}

        -- Картинка с предметами --
        <div className="relative w-full max-w-[480px] aspect-square mb-16">
          <Image
              src="/illustrations/subjects-network.png"
              alt="Subjects network"
              fill
              className="object-contain"
              
            />
          -- Локализованные подписи поверх картинки --
          -- Координаты в % — настраиваются под исходник --
          <span className="absolute left-[15%]  top-[27%] text-[11px] font-semibold tracking-wider text-cyan-300/90 uppercase">{s("math")}</span>
          <span className="absolute right-[27%] top-[27%] text-[11px] font-semibold tracking-wider text-emerald-300/90 uppercase">{s("chemistry")}</span>
          <span className="absolute left-[6%]   top-[57%] text-[11px] font-semibold tracking-wider text-rose-300/90 uppercase">{s("medicine")}</span>
          <span className="absolute right-[18%]  top-[57%] text-[11px] font-semibold tracking-wider text-purple-300/90 uppercase">{s("psychology")}</span>
          <span className="absolute left-[16%]  top-[81%] text-[11px] font-semibold tracking-wider text-amber-200/90 uppercase">{s("history")}</span>
          <span className="absolute right-[30%] top-[81%] text-[11px] font-semibold tracking-wider text-teal-300/90 uppercase">{s("biology")}</span>
        </div>
*/}

 {/*       -- Tagline снизу --
        <div className="absolute bottom-6 left-0 right-0 text-center px-8">
          <p className="text-white/90 text-sm font-medium tracking-wide">
            {s("tagline")}
          </p>
        </div>
      </div> */}  

      <div className="hidden lg:flex flex-1 bg-zinc-50 items-center justify-center relative overflow-hidden h-screen sticky top-0">
                        
                      <Image
                          src="/illustrations/subjects-network.png"
                          alt="Subjects network"
                          fill
                          className="object-contain"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          priority={false}
                        />
                        
                        {/* Hexagon decorations */}
                        <HexLive className="absolute right-[8%]  top-24      w-20 h-20" mode="pulse" baseOpacity={0.15} intervalSec={42} />
                        <HexLive className="absolute right-[15%] bottom-12   w-32 h-32" mode="pulse" baseOpacity={0.10} intervalSec={55} />
                        <HexLive className="absolute right-[5%]  bottom-32   w-12 h-12" mode="spark" baseOpacity={0.20} intervalSec={32} />
                        <HexLive className="absolute left-[3%]  bottom-20   w-16 h-16" mode="spark" baseOpacity={0.12} intervalSec={38} />
                        <HexLive className="absolute left-[10%] top-16       w-24 h-24" mode="pulse" baseOpacity={0.08} intervalSec={60} />
                        <HexLive className="absolute left-[20%] top-[45%]    w-10 h-10" mode="spark" baseOpacity={0.18} intervalSec={28} />
                        
                             
      </div>

      </div>
  
  );
}